import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getVisionFunnel } from "@/lib/vision-funnel.functions";
import { supabase } from "@/integrations/supabase/client";
import { toCsv, downloadCsv } from "@/lib/csv-export";
import { PERF_THRESHOLDS } from "@/lib/web-vitals";

/** Funnel order — each step is a strict subset of the one before it. */
const STEPS = [
  { key: "vision_cta_click", label: "CTA click (/vision)" },
  { key: "purchase_preselected", label: "Purchase preselected" },
  { key: "swap_submitted", label: "Swap submitted" },
  { key: "swap_confirmed", label: "Swap confirmed" },
  { key: "receipt_viewed", label: "Receipt viewed" },
] as const;

type Counts = Record<string, number>;

type Funnel = {
  days: number;
  steps: Counts;
  sessions: Counts;
  by_lang: Record<string, Counts>;
  by_token: Record<string, Counts>;
  failure_codes: Counts;
  daily: { day: string; event: string; count: number }[];
  perf: { samples?: number; lcp_p75?: number; cls_p75?: number; image_p75?: number };
  alerts: Counts;
};

export const Route = createFileRoute("/admin/funnel")({
  component: FunnelDashboard,
  head: () => ({
    meta: [
      { title: "Vision funnel — SFN (internal)" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const pct = (n: number, base: number) => (base > 0 ? Math.round((n / base) * 1000) / 10 : 0);

function FunnelDashboard() {
  const [days, setDays] = useState(30);
  const [lang, setLang] = useState("");
  const [token, setToken] = useState("");
  const [data, setData] = useState<Funnel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const fetchFunnel = useServerFn(getVisionFunnel);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: s }) => {
      if (!cancelled) setSignedIn(Boolean(s.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setSignedIn(Boolean(session)),
    );
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (signedIn !== true) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFunnel({ data: { days, lang, token } })
      .then((result) => {
        if (cancelled) return;
        setData(result as unknown as Funnel);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load the funnel — your session may have expired.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, lang, token, fetchFunnel, signedIn]);

  const steps = data?.steps ?? {};
  const entry = steps["vision_cta_click"] ?? 0;
  const failed = steps["swap_failed"] ?? 0;

  const rows = useMemo(
    () =>
      STEPS.map((s, i) => {
        const value = steps[s.key] ?? 0;
        const prev = i === 0 ? value : (steps[STEPS[i - 1]!.key] ?? 0);
        return {
          ...s,
          value,
          ofEntry: pct(value, entry),
          ofPrev: i === 0 ? 100 : pct(value, prev),
          drop: i === 0 ? 0 : Math.max(prev - value, 0),
        };
      }),
    [steps, entry],
  );

  const exportCsv = useCallback(() => {
    if (!data) return;
    const out: (string | number | null)[][] = [
      ["SFN /vision funnel export"],
      ["generated_at", new Date().toISOString()],
      ["range", `last ${data.days} days`],
      ["locale_filter", lang || "(all)"],
      ["token_filter", token || "(all)"],
      [],
      ["section", "key", "value"],
    ];
    STEPS.forEach((s) => out.push(["funnel", s.key, data.steps?.[s.key] ?? 0]));
    out.push(["funnel", "swap_failed", data.steps?.["swap_failed"] ?? 0]);
    Object.entries(data.sessions ?? {}).forEach(([k, v]) => out.push(["sessions", k, v]));
    Object.entries(data.by_lang ?? {}).forEach(([l, c]) =>
      Object.entries(c).forEach(([k, v]) => out.push([`lang:${l}`, k, v])),
    );
    Object.entries(data.by_token ?? {}).forEach(([tk, c]) =>
      Object.entries(c).forEach(([k, v]) => out.push([`token:${tk}`, k, v])),
    );
    Object.entries(data.failure_codes ?? {}).forEach(([k, v]) =>
      out.push(["failure_code", k, v]),
    );
    Object.entries(data.perf ?? {}).forEach(([k, v]) => out.push(["perf", k, v ?? 0]));
    Object.entries(data.alerts ?? {}).forEach(([k, v]) => out.push(["perf_alert", k, v]));
    downloadCsv(`sfn-vision-funnel-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(out));
  }, [data, lang, token]);

  const selectCls =
    "rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-saffron/50 focus:outline-none";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-saffron">
          // Internal · Hidden route
        </p>
        <h1 className="mt-2 font-display text-4xl font-medium tracking-tight">
          /vision Conversion Funnel
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          CTA click → preselection → submit → confirmed/failed → receipt, attributed to the
          /vision surface and broken down by locale and token. Aggregates only — no PII.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition ${
                  days === d
                    ? "bg-gradient-to-br from-saffron to-coral text-obsidian"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <select
            aria-label="Locale"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className={selectCls}
          >
            <option value="">All locales</option>
            {["en", "de", "zh", "ja", "ko", "ru", "pt"].map((l) => (
              <option key={l} value={l}>
                {l.toUpperCase()}
              </option>
            ))}
          </select>
          <select
            aria-label="Token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className={selectCls}
          >
            <option value="">Both tokens</option>
            <option value="MEAL2">MEAL2</option>
            <option value="MEAL4">MEAL4</option>
          </select>
          <button
            type="button"
            aria-label="Download the conversion funnel as a CSV file"
            onClick={exportCsv}
            disabled={!data}
            className="rounded-lg border border-white/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground transition hover:text-foreground disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>

        {signedIn === false && (
          <p className="mt-10 rounded-2xl glass p-6 text-sm text-muted-foreground">
            Sign in with an admin account to view this report.
          </p>
        )}
        {error && <p className="mt-10 rounded-2xl glass p-6 text-sm text-coral">{error}</p>}
        {loading && signedIn === true && (
          <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
        )}

        {data && !loading && (
          <>
            <section className="mt-10 rounded-2xl glass p-6" aria-labelledby="funnel-h">
              <h2 id="funnel-h" className="font-display text-lg font-medium">
                Funnel steps
              </h2>
              <ol className="mt-5 space-y-3">
                {rows.map((r) => (
                  <li key={r.key}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium">{r.label}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {r.value.toLocaleString()} · {r.ofEntry}% of entry · {r.ofPrev}% step
                        {r.drop > 0 && ` · −${r.drop.toLocaleString()} drop-off`}
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-saffron to-coral"
                        style={{ width: `${Math.max(r.ofEntry, entry > 0 ? 1 : 0)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-5 text-xs text-muted-foreground">
                Failed settlements: <strong className="text-foreground">{failed}</strong> ·
                end-to-end conversion:{" "}
                <strong className="text-foreground">
                  {pct(steps["receipt_viewed"] ?? 0, entry)}%
                </strong>
              </p>
              {Object.keys(data.failure_codes ?? {}).length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(data.failure_codes).map(([code, n]) => (
                    <li
                      key={code}
                      className="rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-muted-foreground"
                    >
                      {code}: {n}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Breakdown title="By locale" data={data.by_lang} />
              <Breakdown title="By token" data={data.by_token} />
            </div>

            <section className="mt-6 rounded-2xl glass p-6" aria-labelledby="perf-h">
              <h2 id="perf-h" className="font-display text-lg font-medium">
                /vision field performance (p75)
              </h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-4">
                <Metric
                  label="LCP"
                  value={data.perf?.lcp_p75 != null ? `${Math.round(data.perf.lcp_p75)} ms` : "—"}
                  bad={(data.perf?.lcp_p75 ?? 0) > PERF_THRESHOLDS.lcp[1]}
                />
                <Metric
                  label="CLS"
                  value={data.perf?.cls_p75 != null ? String(data.perf.cls_p75) : "—"}
                  bad={(data.perf?.cls_p75 ?? 0) > PERF_THRESHOLDS.cls[1]}
                />
                <Metric
                  label="Slowest image"
                  value={
                    data.perf?.image_p75 != null ? `${Math.round(data.perf.image_p75)} ms` : "—"
                  }
                  bad={(data.perf?.image_p75 ?? 0) > PERF_THRESHOLDS.image[1]}
                />
                <Metric label="Samples" value={String(data.perf?.samples ?? 0)} />
              </div>
              {Object.keys(data.alerts ?? {}).length > 0 && (
                <p className="mt-4 text-xs text-coral">
                  Threshold alerts:{" "}
                  {Object.entries(data.alerts)
                    .map(([m, n]) => `${m} ×${n}`)
                    .join(" · ")}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-1 font-display text-xl font-semibold ${bad ? "text-coral" : "text-foreground"}`}
      >
        {value}
      </div>
    </div>
  );
}

function Breakdown({ title, data }: { title: string; data: Record<string, Counts> }) {
  const keys = Object.keys(data ?? {}).sort();
  return (
    <section className="rounded-2xl glass p-6">
      <h2 className="font-display text-lg font-medium">{title}</h2>
      {keys.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No data in this range.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Key</th>
                {STEPS.map((s) => (
                  <th key={s.key} className="py-1.5 pr-3 font-mono font-medium">
                    {s.key.replace("vision_", "").replace("_", " ")}
                  </th>
                ))}
                <th className="py-1.5 font-mono font-medium">conv.</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const c = data[k] ?? {};
                return (
                  <tr key={k} className="border-t border-white/[0.06]">
                    <td className="py-1.5 pr-3 font-semibold uppercase">{k}</td>
                    {STEPS.map((s) => (
                      <td key={s.key} className="py-1.5 pr-3 font-mono">
                        {c[s.key] ?? 0}
                      </td>
                    ))}
                    <td className="py-1.5 font-mono">
                      {pct(c["receipt_viewed"] ?? 0, c["vision_cta_click"] ?? 0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
